import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Input } from 'components/ui/input';
import {
  SidebarMenuAction,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from 'components/ui/sidebar';
import trim from 'lodash/trim';
import { MessageSquare, MoreHorizontal, Pencil, Share2, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import type { ChatSummary } from '../hooks/use-chats';
import { useDeleteChat, useRenameChat } from '../hooks/use-chats';
import { ShareDialog } from './ShareDialog';

interface ChatSidebarItemProps {
  chat: ChatSummary;
  isActive: boolean;
}

export function ChatSidebarItem ({ chat, isActive }: ChatSidebarItemProps) {
  const history = useHistory();
  const renameChat = useRenameChat();
  const deleteChat = useDeleteChat();

  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const displayTitle = chat.title || 'New Chat';

  useEffect(() => {
    if (isRenaming) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isRenaming]);

  const handleStartRename = useCallback(() => {
    setRenameValue(displayTitle);
    setIsRenaming(true);
  }, [displayTitle]);

  const handleSaveRename = useCallback(() => {
    const trimmed = trim(renameValue);
    if (trimmed && trimmed !== displayTitle) {
      renameChat.mutate({ id: chat.id, title: trimmed });
    }
    setIsRenaming(false);
  }, [renameValue, displayTitle, renameChat, chat.id]);

  const handleRenameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSaveRename();
      } else if (e.key === 'Escape') {
        setIsRenaming(false);
      }
    },
    [handleSaveRename],
  );

  const handleDelete = useCallback(() => {
    deleteChat.mutate(chat.id);
    setShowDeleteDialog(false);
    if (isActive) {
      history.push('/chat');
    }
  }, [deleteChat, chat.id, isActive, history]);

  return (
    <>
      <SidebarMenuSubItem className="group/menu-item relative">
        {isRenaming ? (
          <div className="flex h-7 min-w-0 -translate-x-px items-center gap-2 rounded-md px-2 text-xs">
            <MessageSquare className="size-4 shrink-0 opacity-50" />
            <Input
              ref={inputRef}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={handleSaveRename}
              onKeyDown={handleRenameKeyDown}
              className="h-6 text-sm px-1 py-0"
            />
          </div>
        ) : (
          <>
            <SidebarMenuSubButton asChild size="sm" isActive={isActive}>
              <a href={`/chat/${chat.id}`}>
                <MessageSquare className="opacity-50" />
                <span className="flex-1 truncate min-w-0" title={displayTitle}>
                  {displayTitle}
                </span>
              </a>
            </SidebarMenuSubButton>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                {/* top-1 centers the 20px action on the h-7 sub-row; SidebarMenuAction's
                   default top-1.5 assumes a peer/menu-button sibling, which sub-buttons
                   are not. opacity-100 keeps the trigger visible on the active row. */}
                <SidebarMenuAction
                  showOnHover
                  aria-label="Chat actions"
                  className={isActive ? 'top-1 opacity-100' : 'top-1'}
                >
                  <MoreHorizontal />
                </SidebarMenuAction>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-36">
                <DropdownMenuItem
                  onClick={handleStartRename}
                  className="text-xs text-sidebar-foreground focus:bg-sidebar-accent focus:text-sidebar-accent-foreground [&>svg]:text-sidebar-accent-foreground"
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setShowShareDialog(true)}
                  className="text-xs text-sidebar-foreground focus:bg-sidebar-accent focus:text-sidebar-accent-foreground [&>svg]:text-sidebar-accent-foreground"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setShowDeleteDialog(true)}
                  className="text-xs text-destructive focus:bg-sidebar-accent focus:text-destructive [&>svg]:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </SidebarMenuSubItem>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete chat?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete &quot;{displayTitle}&quot;. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ShareDialog open={showShareDialog} onOpenChange={setShowShareDialog} chat={chat} />
    </>
  );
}
